import { Group } from './group'

/**
 * The variable on the Modbus server that will be read or written to.
 */
class IO<T extends InterpretModbusDataType> {
    public constructor(
        public readonly type: T,
        public readonly name: string,
        /** the group the io belogns to */
        public readonly group: Group,
        /** End address of the io value. Let's have an INT16 variable at address 0x04.
         * If we want to write the 2 byte integer, writeOffsetGap must be set to 0x06,
         * since two bytes 0x04 and 0x05 will be written. This is to prevent unwanted
         * overwriting, if you wanted to write a value 2**30 to this io, you would
         * be writing more than 2 bytes and changing the value of some adjacent variable.
         */
        public readonly writeOffsetGap: number,
        /** offset from the start of the group */
        public readonly dataOffset: number,
        /** bit offset of the io (relevant only for boolean variable) from the byte at <group offset> + dataOffset */
        public readonly bitOffset: number
    ) {}
}

const INTERPRET_DATA_TYPE = {
    Bool: 'Bool',
    Int16: 'Int16',
    UInt16: 'UInt16',
    Int32: 'Int32',
    UInt32: 'UInt32',
    Int64: 'Int64',
    UInt64: 'UInt64',
    Float32: 'Float32',
    Float64: 'Float64',
} as const

/** The numeric data type of a Modbus variable. */
type InterpretModbusDataType = keyof typeof INTERPRET_DATA_TYPE

const ENDIANITY = {
    BE: 'BE',
    LE: 'LE',
} as const

/** Endianity of the Modbus client/server. */
type Endianity = keyof typeof ENDIANITY

/** Map a numeric type used in Modbus to javascript numeric type. */
type NumberFromModbus<T extends InterpretModbusDataType> = T extends 'Bool'
    ? boolean
    : T extends 'Int64' | 'UInt64'
      ? bigint
      : number

type TransformModbusDataTypeToMethod<T extends Exclude<InterpretModbusDataType, 'Bool'>> = T extends 'Float32'
    ? 'Float'
    : T extends 'Float64'
      ? 'Double'
      : T extends 'Int64' | 'UInt64'
        ? `Big${T}`
        : T

/** Type-wise map the type to a read/write method name used by native javascript Buffer. */
type GetMethodNameFromModbusDataType<
    T extends Exclude<InterpretModbusDataType, 'Bool'>,
    E extends Endianity,
> = `${TransformModbusDataTypeToMethod<T>}${E}`

/** Map the type to a read/write method name used by native javascript Buffer. */
function getNameForBufferOperations<T extends Exclude<InterpretModbusDataType, 'Bool'>, E extends Endianity>(
    type: T,
    endianity: E
): GetMethodNameFromModbusDataType<T, E> {
    type ReturnType = GetMethodNameFromModbusDataType<T, E>
    if (type === 'Float32') return `Float${endianity}` as ReturnType
    if (type === 'Float64') return `Double${endianity}` as ReturnType
    if (type.endsWith('Int64')) return `Big${type}${endianity}` as ReturnType
    return `${type}${endianity}` as ReturnType
}

type _IOValuePairs = {
    [G in InterpretModbusDataType]: [IO<G>, NumberFromModbus<G>]
}

type IOValuePair = _IOValuePairs[keyof _IOValuePairs]

/** Minimal information for a Modbus variable -- it's name and type. */
type MinimalIO<T extends InterpretModbusDataType> = {
    name: string
    type: T
}

export {
    IO,
    InterpretModbusDataType,
    NumberFromModbus,
    GetMethodNameFromModbusDataType,
    Endianity,
    IOValuePair,
    getNameForBufferOperations,
    MinimalIO,
}
